import { Alert, Paper, Box, Pagination } from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  type GridColDef,
  type GridRowSelectionModel,
} from "@mui/x-data-grid";
import { type DataGridPropsWithoutDefaultValue } from "@mui/x-data-grid/internals";
import { type QueryKey } from "react-query";
import type { Identifiable } from "../../api";
import type { DataLoader, DataRequestState, Filter } from "./types";
import useCustomDatatable from "./useCustomDatatable";

export type Props<TElement extends Identifiable, TFilter extends Filter> = {
  queryKey: QueryKey;
  loadData: DataLoader<TFilter, TElement>;
  columns: Array<GridColDef<TElement>>;
  request: DataRequestState<TFilter>;
  size?: "small" | "large";
  name?: string;
  setFiltered: (filtered: string) => void;
  disableRowSelectionOnClick?: boolean;
  processRowUpdate?: (updatedRow: any, originalRow: any) => any;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  defaultPageSize?: number;
} & Pick<DataGridPropsWithoutDefaultValue<TElement>, "onRowClick">;

export default function DataTable<
  TElement extends Identifiable,
  TFilter extends Filter
>({
  columns,
  queryKey,
  loadData,
  request,
  ...props
}: Props<TElement, TFilter>) {
  const {
    sort,
    currentPage,
    currentLimit,
    tableResults,
    filterModel,
    handlePaginationModelOnChange,
    handlePaginationOnChange,
    handleSortModelOnChange,
    handleFilterModelOnChange,
    toPaginationModel,
    toSortModel,
    useLocaleText,
  } = useCustomDatatable({
    queryKey: queryKey,
    loadData: loadData,
    tableName: props.name ?? "datatable",
    defaultPageSize: props.defaultPageSize ?? 25,
    request: request,
    setFiltered: props.setFiltered,
  });

  return (
    <Paper
      sx={{
        width: "100%",
        display: "table",
        tableLayout: "fixed",
      }}
    >
      {tableResults.error ? (
        <Alert severity="error">{(tableResults.error as any).message}</Alert>
      ) : null}
      <DataGrid
        autoHeight
        disableRowSelectionOnClick={props.disableRowSelectionOnClick}
        processRowUpdate={props.processRowUpdate}
        columns={columns}
        rows={tableResults.data?.items ?? []}
        rowCount={tableResults.data?.totalElements ?? 0}
        paginationMode="server"
        filterMode={"server"}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelOnChange}
        paginationModel={toPaginationModel(currentPage, currentLimit)}
        onPaginationModelChange={handlePaginationModelOnChange}
        sortingMode="server"
        sortModel={toSortModel(sort)}
        onSortModelChange={handleSortModelOnChange}
        loading={tableResults.isLoading}
        localeText={useLocaleText()}
        slots={{ toolbar: GridToolbar }}
        rowHeight={props.size?.toLowerCase() === "large" ? 55 : 38}
        sx={{ userSelect: "none" }}
        {...props}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "16px",
        }}
      >
        <Pagination
          count={Math.ceil(
            (tableResults.data?.totalElements ?? 0) / currentLimit
          )}
          page={currentPage + 1}
          onChange={(_, page: number) => handlePaginationOnChange(page)}
        />
      </Box>
    </Paper>
  );
}
